import {
	IconCircleCheck,
	IconCircleX,
	IconDownload,
	IconRefresh,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import {
	Alert,
	Button,
	Checkbox,
	Descriptions,
	Flex,
	Form,
	Grid,
	Input,
	Modal,
	Progress,
	Segmented,
	Select,
	Space,
	Spin,
	Steps,
	Switch,
	Typography,
	theme,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import {
	type EnvironmentConnectivityResult,
	useCreateEnvironment,
	useGenerateSetupTemplate,
	useUpdateEnvironment,
	useVerifyEnvironmentConnection,
} from "@/common/artemis-operations/environments";
import {
	ENVIRONMENT_CREATED_DESC,
	ENVIRONMENT_CREATED_TITLE,
	ERROR_ENVIRONMENT_CREATION_DESC,
	ERROR_ENVIRONMENT_CREATION_TITLE,
} from "@/common/messages";
import {
	AutoApprovalThreshold,
	EnvironmentClassification,
	EnvironmentProvider,
	EnvironmentStatus,
} from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { CustomIcon } from "@/components/base";
import { EditableText } from "@/components/base-details";
import { AutomationPolicyField } from "@/components/environments/automation-policy-ladder";
import { AwsPermissionsPicker } from "@/components/environments/aws-permissions-picker";
import {
	DEFAULT_SERVICE_KEYS,
	resolveServiceActions,
} from "@/components/environments/aws-service-catalog";
import {
	AutoApprovalThresholdBadge,
	EnvironmentClassificationBadge,
	EnvironmentProviderBadge,
} from "@/components/environments/environment-badges";
import {
	AWS_ROLE_ARN_METADATA_KEYS,
	type AwsRoleKey,
	awsRoleArn,
	buildAwsRoleNames,
	CLASSIFICATION_OPTIONS,
	generateExternalId,
	generateRoleSuffix,
	PROVIDER_ITEMS,
	PROVIDER_METADATA_FIELDS,
	providerDocsUrl,
} from "@/components/environments/environment-base";
import {
	collectMetadata,
	MetadataFieldItem,
} from "@/components/environments/environment-metadata-fields";
import { SegmentedSelection } from "@/components/segmented-selection";

/** Numbered CloudFormation deploy steps shown on the guided "CloudFormation Template" step. */
const DEPLOY_STEPS = [
	"Open the AWS CloudFormation console in your target account and region.",
	"Choose Create stack → Upload the downloaded template file.",
	'On "Specify stack details", give the stack a name - there are no parameters to fill.',
	'Check "I acknowledge that AWS CloudFormation might create IAM resources with custom names."',
	"Create the stack and wait for CREATE_COMPLETE (usually 1–2 minutes).",
	"The role ARNs are filled in for you on the next step - deploy the stack, then continue.",
];

/** AWS metadata keys that are account identity (not roles) - they get their own wizard step. */
const AWS_IDENTITY_KEYS = new Set(["AccountId", "DefaultRegion"]);

function downloadYaml(content: string, filename: string) {
	const blob = new Blob([content], { type: "application/x-yaml" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

type ConnectionMode = "guide" | "manual";

interface WizardStep {
	key: string;
	title: string;
	/** Field names validated before advancing past this step. */
	fields?: string[];
	/** Greyed out in the rail; the step is shown but not needed for the current choices. */
	disabled?: boolean;
	content: React.ReactNode;
}

/**
 * Single end-to-end onboarding wizard for creating an environment: basics (name, provider, and -
 * for AWS - the guided-vs-manual choice, asked immediately) → account → (guided only) agent
 * permissions & generate/deploy the tailored CloudFormation template, which always includes both
 * account access and agent hosting roles → IAM roles → policy (classification + automation) →
 * review & verify. A wide centered Modal with a vertical step rail. Picking "I already have the
 * connection details" on Basics drops the guided-only steps from the rail entirely (rather than
 * showing them disabled) and asks for the External ID at the top of the IAM roles step, alongside
 * the pasted role ARNs.
 */
export function EnvironmentOnboardingWizard({
	open,
	onClose,
	onCreated,
}: {
	open: boolean;
	onClose: () => void;
	/** Called once, after the environment is created and the wizard is closed (not on cancel). */
	onCreated?: (environment: {
		id: string;
		provider: EnvironmentProvider;
	}) => void;
}) {
	const { token } = theme.useToken();
	const screens = Grid.useBreakpoint();
	const isMobile = !screens.md;
	const { workspaceId } = useParams({ strict: false }) as {
		workspaceId: string;
	};
	const [form] = Form.useForm();
	const notify = useNotify();
	const queryClient = useQueryClient();
	const createEnvironment = useCreateEnvironment();
	const { generate, isPending: generating } = useGenerateSetupTemplate();

	const [stepIndex, setStepIndex] = useState(0);
	const [creating, setCreating] = useState(false);
	const [createdEnvId, setCreatedEnvId] = useState<string | null>(null);
	// Gates "Done": the mandatory connectivity check must finish before the wizard can close.
	const [verifyDone, setVerifyDone] = useState(false);
	// Generated once per open: the external id is baked into the template; the role names get a
	// random suffix so they stay unique across environments in the same AWS account.
	const [externalId, setExternalId] = useState("");
	const [roleSuffix, setRoleSuffix] = useState("");
	// AWS services the operator roles may touch. Asked once: the read half of each service goes to
	// the baseline role, read + write to the escalation ceiling. Splitting it into two questions
	// asked the same thing twice and let the baseline end up as wide as the ceiling.
	const [opsServices, setOpsServices] =
		useState<string[]>(DEFAULT_SERVICE_KEYS);
	// Read-only is the default: agents observe but cannot change anything. Granting writes is an
	// explicit opt-in, and either way the customer owns both roles in IAM afterwards.
	const [readOnlyAccess, setReadOnlyAccess] = useState(true);
	// How the customer deploys the generated template on the "CloudFormation Template" step.
	const [deployMethod, setDeployMethod] = useState<"console" | "cli">(
		"console",
	);

	useEffect(() => {
		if (open) {
			setStepIndex(0);
			setCreating(false);
			setCreatedEnvId(null);
			setVerifyDone(false);
			setExternalId(generateExternalId());
			setRoleSuffix(generateRoleSuffix());
			setOpsServices(DEFAULT_SERVICE_KEYS);
			setReadOnlyAccess(true);
			setDeployMethod("console");
			form.resetFields();
		}
	}, [open, form]);

	const provider = (Form.useWatch("provider", form) ??
		EnvironmentProvider.AWS) as EnvironmentProvider;
	const connectionMode = Form.useWatch("connectionMode", form) as
		| ConnectionMode
		| undefined;
	const accountId = Form.useWatch("meta_AccountId", form) as string | undefined;
	const formValues = Form.useWatch((vals) => vals, form) ?? {};
	const isAws = provider === EnvironmentProvider.AWS;
	// Every generated template includes the full set of roles - no longer a wizard choice.
	const agentAccess = true;
	const agentDeployment = true;
	const roleNames = useMemo(() => buildAwsRoleNames(roleSuffix), [roleSuffix]);

	// Guided AWS path: keep the external id + role ARN fields in sync with the generated values so
	// the "Connect roles" step is pre-filled. Manual path leaves the fields for the user to paste.
	useEffect(() => {
		if (!isAws || connectionMode !== "guide") return;
		const updates: Record<string, string> = {
			meta_AgentOperationsExternalId: externalId,
		};
		if (accountId && /^\d{12}$/.test(accountId)) {
			const included: AwsRoleKey[] = [
				"connection",
				"operations",
				"operationsMax",
				"runtime",
				"deployment",
			];
			for (const roleKey of included)
				updates[`meta_${AWS_ROLE_ARN_METADATA_KEYS[roleKey]}`] = awsRoleArn(
					accountId,
					roleNames[roleKey],
				);
		}
		form.setFieldsValue(updates);
	}, [isAws, connectionMode, accountId, externalId, roleNames, form]);

	// Manual path: the user brings their own connection details, so clear anything the guided path
	// pre-filled (external id + role ARNs). Keyed on connectionMode so it fires once on the switch and
	// never wipes what the user then types in.
	useEffect(() => {
		if (!isAws || connectionMode !== "manual") return;
		const cleared: Record<string, undefined> = {
			meta_AgentOperationsExternalId: undefined,
		};
		for (const metaKey of Object.values(AWS_ROLE_ARN_METADATA_KEYS))
			cleared[`meta_${metaKey}`] = undefined;
		form.setFieldsValue(cleared);
	}, [isAws, connectionMode, form]);

	const metadataFields = PROVIDER_METADATA_FIELDS[provider] ?? [];
	const identityFields = isAws
		? metadataFields.filter((f) => AWS_IDENTITY_KEYS.has(f.key))
		: metadataFields;
	// AWS role ARN fields visible on the "IAM roles" step, plus the External ID on the manual path
	// (guided bakes it into the template instead). Secret fields sort first so External ID renders
	// at the top, above the pasted role ARNs.
	const roleFields = metadataFields
		.filter((f) => {
			if (AWS_IDENTITY_KEYS.has(f.key)) return false;
			if (!f.group) return true; // ConnectionRoleArn - always
			if (f.group === "agentAccess")
				return f.secret ? connectionMode === "manual" : agentAccess;
			if (f.group === "agentDeployment") return agentDeployment;
			return false;
		})
		.sort((a, b) => Number(b.secret ?? false) - Number(a.secret ?? false));

	const downloadTemplate = async () => {
		const operationsActions = resolveServiceActions(opsServices, "read");
		// Read-only keeps the ceiling at the baseline: nothing an approval could ever unlock writes.
		const operationsMaxActions = resolveServiceActions(
			opsServices,
			readOnlyAccess ? "read" : "readwrite",
		);
		// The operator roles need at least one action when Agent Access is on (backend enforces this).
		if (
			agentAccess &&
			(operationsActions.length === 0 || operationsMaxActions.length === 0)
		) {
			notify.error(
				"Select at least one service",
				"The operator roles need at least one AWS service on the Role permissions step.",
			);
			return;
		}
		try {
			const template = await generate({
				provider: EnvironmentProvider.AWS,
				input: {
					externalId,
					enableAgentAccess: agentAccess,
					enableAgentDeployment: agentDeployment,
					connectionRoleName: roleNames.connection,
					operationsRoleName: roleNames.operations,
					operationsMaxRoleName: roleNames.operationsMax,
					runtimeRoleName: roleNames.runtime,
					deploymentRoleName: roleNames.deployment,
					operationsActions,
					operationsMaxActions,
				},
			});
			downloadYaml(template.content, template.fileName);
		} catch {
			notify.error("Could not generate the template", "Please try again.");
		}
	};

	const selectedThreshold = (formValues.autoApproveUpTo ??
		AutoApprovalThreshold.MEDIUM) as AutoApprovalThreshold;

	// ── Step contents ─────────────────────────────────────────────────────────
	const basicsStep: WizardStep = {
		key: "details",
		title: "Details",
		fields: ["name", "provider", ...(isAws ? ["connectionMode"] : [])],
		content: (
			<>
				<Form.Item
					name="name"
					label="Name"
					rules={[
						{ required: true },
						{ max: 255, message: "Name must be 255 characters or less" },
					]}
				>
					<Input placeholder="Environment name" maxLength={255} />
				</Form.Item>
				<Form.Item
					name="provider"
					label="Provider"
					initialValue={EnvironmentProvider.AWS}
					rules={[{ required: true }]}
				>
					<Select
						placeholder="Select a provider"
						options={PROVIDER_ITEMS}
						optionRender={(option) => (
							<Flex align="center" gap={8}>
								{option.data.icon}
								<Typography.Text>{option.data.label}</Typography.Text>
								{option.data.disabled && (
									<Typography.Text type="secondary">
										(coming soon...)
									</Typography.Text>
								)}
							</Flex>
						)}
					/>
				</Form.Item>
				{isAws && (
					<Form.Item
						name="connectionMode"
						label="Is this account already connected to StratoNext?"
						initialValue={"guide" satisfies ConnectionMode}
						rules={[{ required: true }]}
					>
						<SegmentedSelection<ConnectionMode>
							columns={1}
							options={[
								{
									value: "guide",
									label: "Guide me through setup",
									description:
										"Generate the required setup assets to connect the account to StratoNext.",
								},
								{
									value: "manual",
									label: "I already have the connection details",
									description: "The account is already connected.",
								},
							]}
						/>
					</Form.Item>
				)}
			</>
		),
	};

	const accountStep: WizardStep = {
		key: "account",
		title: "Account",
		fields: [
			...identityFields.map((f) => `meta_${f.key}`),
			...(isAws ? ["prereqAck"] : []),
		],
		content: (
			<>
				{identityFields.map((field) => (
					<MetadataFieldItem key={field.key} field={field} />
				))}
				{isAws && (
					<Alert
						type="warning"
						title="Before you continue"
						description={
							<>
								Connecting this account needs credentials for the target AWS
								account, and some manual prerequisites (e.g. enabling Amazon
								Bedrock model access). See the{" "}
								<Typography.Link
									href={providerDocsUrl(provider)}
									target="_blank"
								>
									account setup guide
								</Typography.Link>{" "}
								and complete these before continuing.
								<Form.Item
									name="prereqAck"
									valuePropName="checked"
									initialValue={false}
									style={{ marginTop: 16, marginBottom: 0 }}
									rules={[
										{
											validator: (_, checked) =>
												checked
													? Promise.resolve()
													: Promise.reject(
															new Error(
																"Confirm the prerequisites to continue",
															),
														),
										},
									]}
								>
									<Checkbox>I've completed the prerequisites.</Checkbox>
								</Form.Item>
							</>
						}
					/>
				)}
			</>
		),
	};

	const manualMode = isAws && connectionMode === "manual";

	// `aws cloudformation deploy` command for the CLI deploy option. Region comes from the account
	// step; CAPABILITY_NAMED_IAM is required because the template creates named IAM roles.
	const deployRegion = (formValues.meta_DefaultRegion as string) || "us-east-1";
	const deployCommand = `aws cloudformation deploy \\
  --template-file stratonext-aws-setup.yaml \\
  --stack-name stratonext-connector \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --region ${deployRegion}`;

	// Operator role permissions only apply while guiding the account setup - a manual-mode customer's
	// roles already exist, and their permissions live in their own AWS IAM console, so this step and
	// the CloudFormation deploy step below are dropped entirely from the rail on that path.
	const rolePermissionsStep: WizardStep = {
		key: "rolePermissions",
		title: "Agent permissions",
		content: (
			<Flex vertical gap={10}>
				<Typography.Paragraph>
					Here you can define the basic permissions available to your Agents.
					StratoNext creates IAM roles for the services you pick below:
					read-only actions are granted to Agents automatically, and anything
					that changes your account requires operator approval first. Anything
					outside these roles is denied. See the{" "}
					<Typography.Link
						href={`${providerDocsUrl(provider)}/iam-guardrails`}
						target="_blank"
					>
						permissions guide
					</Typography.Link>{" "}
					for how to customize this further.
				</Typography.Paragraph>
				<Flex align="center" gap={8}>
					<Switch
						size="small"
						checked={readOnlyAccess}
						// Switching modes changes what a picked service grants, so start the selection over
						// rather than silently carrying write grants across the toggle.
						onChange={(checked) => {
							setReadOnlyAccess(checked);
							setOpsServices(DEFAULT_SERVICE_KEYS);
						}}
					/>
					<Typography.Text>Read-only access</Typography.Text>
				</Flex>
				<Alert
					type={readOnlyAccess ? "success" : "warning"}
					title={
						readOnlyAccess
							? "Read-only is on, the safe default"
							: "Write access is on"
					}
					description={
						readOnlyAccess ? (
							<>
								Read-only is the default and recommended choice. For each
								service you pick, Agents can inspect this account but never
								change it.
							</>
						) : (
							<>
								For each service you pick, Agents can also request changes, a
								human must approve before anything runs. Turn read-only back on
								unless you specifically need agents to make changes in this
								account.
							</>
						)
					}
				/>
				<AwsPermissionsPicker
					value={opsServices}
					onChange={setOpsServices}
					readOnly={readOnlyAccess}
				/>
			</Flex>
		),
	};

	const deployStep: WizardStep = {
		key: "deploy",
		title: "CloudFormation Template",
		fields: ["prereqConfirmed"],
		content: (
			<Flex vertical gap={16}>
				<Flex vertical gap={8} align="flex-start">
					<Typography.Title level={5} style={{ margin: 0 }}>
						Download the template
					</Typography.Title>
					<Typography.Paragraph>
						In order to connect to your account, StratoNext needs some IAM
						Roles. The following template will create them for you. You need to
						download and deploy it manually via the AWS Console or AWS CLI.
					</Typography.Paragraph>
					<Button
						type="primary"
						icon={<CustomIcon icon={IconDownload} />}
						loading={generating}
						onClick={downloadTemplate}
					>
						Download Template
					</Button>
				</Flex>
				<Flex vertical gap={8}>
					<Typography.Title level={5} style={{ margin: 0 }}>
						Deploy the template
					</Typography.Title>
					<Typography.Paragraph>
						Follow the steps below to deploy the template via the AWS Console or
						AWS CLI.
					</Typography.Paragraph>
					<Segmented
						block
						value={deployMethod}
						onChange={(v) => setDeployMethod(v as "console" | "cli")}
						options={[
							{ label: "AWS Console", value: "console" },
							{ label: "AWS CLI", value: "cli" },
						]}
						style={{ marginBottom: 12 }}
					/>
					{deployMethod === "console" ? (
						<Flex vertical gap={10}>
							{DEPLOY_STEPS.map((s, i) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: static ordered list
								<Flex key={i} gap={10} align="flex-start">
									<span
										style={{
											minWidth: 22,
											height: 22,
											borderRadius: "50%",
											background: token.colorFillSecondary,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											fontSize: 11,
											fontWeight: 600,
											flexShrink: 0,
											color: token.colorTextSecondary,
										}}
									>
										{i + 1}
									</span>
									<Typography.Text style={{ paddingTop: 2 }}>
										{s}
									</Typography.Text>
								</Flex>
							))}
						</Flex>
					) : (
						<Flex vertical gap={8}>
							<Typography.Text>
								From the folder where the template downloaded, run:
							</Typography.Text>
							<Typography.Paragraph
								copyable={{ text: deployCommand }}
								style={{
									background: token.colorFillSecondary,
									padding: "10px 12px",
									borderRadius: token.borderRadius,
									fontFamily: "monospace",
									fontSize: 12,
									whiteSpace: "pre-wrap",
									margin: 0,
								}}
							>
								{deployCommand}
							</Typography.Paragraph>
							<Typography.Text>
								Wait for the stack to reach CREATE_COMPLETE, then continue.
							</Typography.Text>
						</Flex>
					)}
				</Flex>
				<Alert
					type="warning"
					title="Before you continue"
					description={
						<>
							The next step reads the role ARNs your stack creates, so the stack
							has to exist first. Wait for it to reach CREATE_COMPLETE.
							<Form.Item
								name="prereqConfirmed"
								valuePropName="checked"
								initialValue={false}
								style={{ marginTop: 16, marginBottom: 0 }}
								rules={[
									{
										validator: (_, checked) =>
											checked
												? Promise.resolve()
												: Promise.reject(
														new Error(
															"Confirm you've deployed the stack to continue",
														),
													),
									},
								]}
							>
								<Checkbox>I've deployed the stack in my AWS account.</Checkbox>
							</Form.Item>
						</>
					}
				/>
			</Flex>
		),
	};

	const rolesStep: WizardStep = {
		key: "roles",
		title: "IAM roles",
		fields: roleFields.map((f) => `meta_${f.key}`),
		content:
			connectionMode === "guide" ? (
				// Guided ARNs are prefilled from the account id + generated role names. Show them as a
				// read-only table; each value is inline-editable (click the pen) if the user tweaked
				// their stack. The hidden Form.Item keeps validation + submission wired.
				<Flex vertical gap={8}>
					<Typography.Paragraph>
						These are the roles your CloudFormation stack creates. Click a value
						to edit it if you changed anything in your stack.
					</Typography.Paragraph>
					<Descriptions column={1} size="small" bordered>
						{roleFields.map((field) => (
							<Descriptions.Item key={field.key} label={field.label}>
								<Form.Item
									name={`meta_${field.key}`}
									noStyle
									rules={[
										...(field.required ? [{ required: true }] : []),
										...(field.pattern
											? [
													{
														pattern: field.pattern,
														message:
															field.patternMessage ?? "Enter a valid AWS ARN",
													},
												]
											: []),
									]}
								>
									<EditableArnCell />
								</Form.Item>
							</Descriptions.Item>
						))}
					</Descriptions>
				</Flex>
			) : (
				<Flex vertical gap={5}>
					<Typography.Paragraph>
						Paste the External ID and role ARNs from your deployed stack's
						Outputs tab.
					</Typography.Paragraph>
					{roleFields.map((field) => (
						<MetadataFieldItem
							key={field.key}
							field={field}
							// External ID is required once any role is configured (the connection role always is).
							required={field.secret ? true : undefined}
						/>
					))}
				</Flex>
			),
	};

	const policyStep: WizardStep = {
		key: "policy",
		title: "Automation Policy",
		fields: ["classification", "autoApproveUpTo"],
		content: (
			<>
				Choose the account classification and automation policy. Production
				accounts are treated with stricter controls and require operator
				approval for Agent operations, while non-production accounts can allow
				greater automation.
				<Form.Item
					name="classification"
					label="Classification"
					initialValue={EnvironmentClassification.NON_PRODUCTION}
					rules={[{ required: true }]}
				>
					<SegmentedSelection options={CLASSIFICATION_OPTIONS} />
				</Form.Item>
				<AutomationPolicyField initialValue={AutoApprovalThreshold.MEDIUM} />
			</>
		),
	};

	const reviewStep: WizardStep = {
		key: "review",
		title: "Review & verify",
		content: createdEnvId ? (
			<VerifyChecklist
				environmentId={createdEnvId}
				onDone={() => setVerifyDone(true)}
			/>
		) : (
			<Descriptions column={1} size="small" bordered>
				<Descriptions.Item label="Name">
					{(formValues.name as string) || "-"}
				</Descriptions.Item>
				<Descriptions.Item label="Provider">
					<EnvironmentProviderBadge type={provider} />
				</Descriptions.Item>
				<Descriptions.Item label="Classification">
					<EnvironmentClassificationBadge
						classification={
							(formValues.classification as EnvironmentClassification) ??
							EnvironmentClassification.NON_PRODUCTION
						}
					/>
				</Descriptions.Item>
				{isAws && (
					<Descriptions.Item label="Capabilities">
						Account access, Host agents
					</Descriptions.Item>
				)}
				<Descriptions.Item label="Automation policy">
					<AutoApprovalThresholdBadge type={selectedThreshold} />
				</Descriptions.Item>
			</Descriptions>
		),
	};

	const steps: WizardStep[] = [
		basicsStep,
		accountStep,
		// Guided-setup-only steps: a manual-mode customer's roles already exist, so template
		// generation and its permissions picker are dropped from the rail entirely rather than
		// shown disabled.
		...(isAws && !manualMode ? [rolePermissionsStep, deployStep] : []),
		...(isAws ? [rolesStep] : []),
		policyStep,
		reviewStep,
	];
	// Changing provider can shorten the step list; keep the index in range.
	const current = Math.min(stepIndex, steps.length - 1);
	const activeStep = steps[current];
	const isReview = activeStep?.key === "review";

	const handleClose = () => {
		// Only a successful "Done" sets createdEnvId; Cancel/X never fires onCreated.
		if (createdEnvId) onCreated?.({ id: createdEnvId, provider });
		onClose();
		form.resetFields();
		setStepIndex(0);
		setCreatedEnvId(null);
		setVerifyDone(false);
	};

	const goNext = () => {
		const fields = activeStep?.fields;
		if (fields?.length)
			form
				.validateFields(fields)
				.then(() => setStepIndex(current + 1))
				.catch(() => {});
		else setStepIndex(current + 1);
	};

	const handleCreate = () => {
		form
			.validateFields()
			.then((vals) => {
				setCreating(true);
				// Guided mode writes the External ID into the store via an effect but never renders a
				// Form.Item for it, so validateFields() omits it. getFieldsValue(true) includes such
				// programmatically-set fields - collect metadata from that, not the validated subset.
				const metadata = collectMetadata(form.getFieldsValue(true), {
					provider,
					isEditMode: false,
				});
				createEnvironment(
					{
						name: vals.name as string,
						provider,
						classification: vals.classification as EnvironmentClassification,
						autoApproveUpTo: vals.autoApproveUpTo as AutoApprovalThreshold,
						workspaceId,
						metadata: metadata.length > 0 ? metadata : undefined,
						allowAgentsToAccess: true,
						allowAgentsToBeDeployed: true,
					},
					{
						onSuccess: (created) => {
							notify.success(
								ENVIRONMENT_CREATED_TITLE,
								ENVIRONMENT_CREATED_DESC,
							);
							queryClient.invalidateQueries({ queryKey: ["environments"] });
							setCreating(false);
							setCreatedEnvId(created.id);
						},
						onError: () => {
							setCreating(false);
							notify.error(
								ERROR_ENVIRONMENT_CREATION_TITLE,
								ERROR_ENVIRONMENT_CREATION_DESC,
							);
						},
					},
				);
			})
			.catch(() => {});
	};

	const footer = createdEnvId ? (
		<Button
			type="primary"
			loading={!verifyDone}
			disabled={!verifyDone}
			onClick={handleClose}
		>
			Done
		</Button>
	) : (
		<Space style={{ width: "100%", justifyContent: "flex-end" }}>
			{current > 0 && (
				<Button disabled={creating} onClick={() => setStepIndex(current - 1)}>
					Back
				</Button>
			)}
			{isReview ? (
				<Button type="primary" loading={creating} onClick={handleCreate}>
					Create & verify
				</Button>
			) : (
				<Button type="primary" onClick={goNext}>
					Next
				</Button>
			)}
		</Space>
	);

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			title="Set up an environment"
			// Full-page: fill the viewport so the multi-column steps have room to breathe.
			width="100vw"
			style={{ top: 0, maxWidth: "100vw", margin: 0, paddingBottom: 0 }}
			footer={footer}
			maskClosable={false}
			styles={{
				container: {
					height: "100vh",
					borderRadius: 0,
					display: "flex",
					flexDirection: "column",
				},
				body: { flex: 1, minHeight: 0, paddingTop: 12, display: "flex" },
			}}
		>
			{/* Desktop: vertical step rail beside the content. Mobile: a compact horizontal
			    progress-dot stepper on top, content stacked below (the rail doesn't fit a phone). */}
			<Flex
				vertical={isMobile}
				gap={isMobile ? 12 : 24}
				style={{ flex: 1, minHeight: 0, width: "100%" }}
			>
				{isMobile ? (
					// A full stepper doesn't fit a phone - show a compact progress header instead.
					<div>
						{/* The step title leads the page itself, so the header only carries progress. */}
						<Flex align="baseline" justify="flex-end">
							<Typography.Text>
								Step {current + 1} of {steps.length}
							</Typography.Text>
						</Flex>
						<Progress
							percent={Math.round(((current + 1) / steps.length) * 100)}
							showInfo={false}
							size="small"
						/>
					</div>
				) : (
					<Steps
						orientation="vertical"
						current={current}
						size="small"
						style={{ width: 220, flexShrink: 0 }}
						items={steps.map((s) => ({ title: s.title, disabled: s.disabled }))}
					/>
				)}
				<div
					style={{
						flex: 1,
						minWidth: 0,
						minHeight: 0,
						overflowY: "auto",
						scrollbarWidth: "none",
						// overflowY:auto forces overflowX to clip too - pad so card hover shadows aren't cropped.
						paddingInline: 18,
					}}
				>
					<Form
						form={form}
						layout="vertical"
						// Full-page gives vertical room; cap width so forms stay readable, not sprawled.
						style={{ maxWidth: 900, marginInline: "auto" }}
					>
						{steps.map((s, i) => (
							<div
								key={s.key}
								style={{ display: i === current ? undefined : "none" }}
							>
								<Typography.Title level={4} style={{ marginTop: 0 }}>
									{s.title}
								</Typography.Title>
								{s.content}
							</div>
						))}
					</Form>
				</div>
			</Flex>
		</Modal>
	);
}

/**
 * Inline-editable role ARN cell for the guided "Connect roles" table. Driven by its wrapping
 * Form.Item (value/onChange injected), so reads as plain text with an edit pen and writes edits
 * straight back to the form - same pattern as the task-details fields.
 */
function EditableArnCell({
	value,
	onChange,
}: {
	value?: string;
	onChange?: (value: string) => void;
}) {
	return (
		<EditableText onChange={onChange} style={{ width: "100%" }}>
			{value ?? ""}
		</EditableText>
	);
}

/**
 * Runs the connectivity check for the freshly-created environment and lists each configured role as
 * a pass/fail check. Rendered as the final wizard step once the environment exists.
 */
function VerifyChecklist({
	environmentId,
	onDone,
}: {
	environmentId: string;
	/** Fired once the check settles (pass or fail) so the wizard can enable "Done". */
	onDone: () => void;
}) {
	const { verify, isPending } = useVerifyEnvironmentConnection(environmentId);
	const updateEnvironment = useUpdateEnvironment(environmentId);
	const [result, setResult] = useState<EnvironmentConnectivityResult | null>(
		null,
	);
	const runCheck = () =>
		verify(undefined, {
			onSuccess: (res) => {
				setResult(res);
				// A role we can't assume means agents can't operate here - mark the environment FAILED so
				// it isn't used until the connection is fixed. Re-test flips it back to ACTIVE once all pass.
				if (res.checks.length > 0)
					updateEnvironment({
						status: res.checks.some((c) => !c.ok)
							? EnvironmentStatus.FAILED
							: EnvironmentStatus.ACTIVE,
					});
			},
			onSettled: onDone,
		});
	const hasFailure = (result?.checks ?? []).some((c) => !c.ok);

	// biome-ignore lint/correctness/useExhaustiveDependencies: run once per environment id.
	useEffect(() => {
		runCheck();
	}, [environmentId]);

	const checks = result?.checks ?? [];
	return (
		<Flex vertical gap={16}>
			<Flex align="center" justify="space-between">
				<Typography.Text>
					Environment created. Checking that StratoNext can assume each
					configured role.
				</Typography.Text>
				<Button
					disabled={isPending}
					icon={<CustomIcon icon={IconRefresh} />}
					onClick={runCheck}
				>
					Check roles
				</Button>
			</Flex>
			{hasFailure && (
				<Alert
					type="warning"
					showIcon
					title="Environment marked failed"
					description="A role check failed, so this environment was marked failed. Fix the connection and re-test to re-enable it."
				/>
			)}
			{isPending && !result ? (
				<Flex justify="center" style={{ padding: 24 }}>
					<Spin />
				</Flex>
			) : checks.length === 0 ? (
				<Typography.Text>No roles are configured to verify.</Typography.Text>
			) : (
				<Flex vertical gap={12}>
					{checks.map((check) => (
						<Flex key={check.role} gap={8} align="flex-start">
							<CustomIcon
								icon={check.ok ? IconCircleCheck : IconCircleX}
								color={
									check.ok
										? "var(--ant-color-success)"
										: "var(--ant-color-error)"
								}
							/>
							<div>
								<Typography.Text strong>{check.role}</Typography.Text>
								{!check.ok && check.message && (
									<div style={{ color: "var(--ant-color-error)" }}>
										{check.message}
									</div>
								)}
							</div>
						</Flex>
					))}
				</Flex>
			)}
		</Flex>
	);
}
